<template>
  <div class="file-list">
    <EmptyStateTable
      v-if="hasNoFiles"
      :heading="t('files_empty_state_headline')"
      :description="isRoom ? t('files_empty_state_copy') : t('template_files_empty_state_copy')"
    >
      <template #cta>
        <FriedButton
          v-if="participantId || hasCollaboratorAccess"
          data-external="files-upload-file-button-empty-state"
          @click="$emit('upload')"
        >
          <template #leftIcon>
            <FriedIcon icon="upload" />
          </template>
          {{ t('Upload file') }}
        </FriedButton>
      </template>
    </EmptyStateTable>
    <EmptyStateTable
      v-else-if="hasNoSearchResults"
      :heading="t('dr_empty_search_result_empty_state_heading')"
      :description="t('dr_files_no_matching_results_description')"
    >
      <template #image>
        <img src="../assets/search-empty.png" :alt="t('dr_empty_search_alt_text')" />
      </template>
    </EmptyStateTable>
    <template v-else>
      <FriedTable class="file-list-table">
        <template #header>
          <FriedTableHeader>
            <FriedTableHeaderCell class="name-header-cell">
              {{ t('files_file_name') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell v-if="isRoom" class="hidden-sm-and-down">
              {{ t('frame_uploaded_by') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell
              class="hidden-sm-and-down"
              :sortable="true"
              data-external="files-sort-document-created-at-button"
              :active="isActiveSortBy(FileSortBy.CreatedAt)"
              :direction="filter.sortOrder"
              @click="handleSort(FileSortBy.CreatedAt)"
            >
              {{ t('uploaded_date') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell
              sortable
              class="hidden-md-and-down"
              :active="isActiveSortBy(FileSortBy.Extension)"
              :direction="filter.sortOrder"
              @click="handleSort(FileSortBy.Extension)"
            >
              {{ t('dr_file_type') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell class="hidden-md-and-down">
              {{ t('size') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell
              v-if="!participantId && isRoom"
              sortable
              class="hidden-md-and-down"
              :active="isActiveSortBy(FileSortBy.NbrOfDownloads)"
              :direction="filter.sortOrder"
              :align="TableAlign.Right"
              @click="handleSort(FileSortBy.NbrOfDownloads)"
            >
              {{ t('files_downloads') }}
            </FriedTableHeaderCell>
            <FriedTableHeaderCell class="menu-cell" :align="TableAlign.Right" />
          </FriedTableHeader>
        </template>
        <template v-if="loading && !hasFiles" #body>
          <FileSkeleton v-for="index in 20" :key="index" :is-edit-site="!participantId" />
        </template>
        <template v-else #body>
          <FriedTableRow
            v-for="file in files"
            :id="file.id"
            :key="file.id"
            :class="{ 'is-pinned': file.pinned }"
            :data-external="`file-list-file-id:${file.id}-table-row`"
          >
            <FriedTableCell>
              <div class="name">
                <FriedIcon v-if="file.pinned" icon="pin" />
                <FriedH4
                  v-tooltip="{
                    content: getTruncatedText(file.name, 120),
                    distance: 5,
                    delay: { show: 600, hide: 100 },
                  }"
                  class="file-name truncate"
                  :data-external="`file-name:${file.name}`"
                  >{{ file.name }}</FriedH4
                >
              </div>
              <div v-if="isRoom" class="hidden-sm-and-up">
                <ParticipantInfo
                  :participant="getFileOwner(file)"
                  :timestamp="file.createdAt"
                  compact
                />
                <FriedStatusBadge :text="file.extension.toUpperCase()" />
              </div>
            </FriedTableCell>
            <FriedTableCell v-if="isRoom" class="hidden-sm-and-down">
              <div class="name">
                <ParticipantInfo :participant="getFileOwner(file)" :show-timestamp="false" />
              </div>
            </FriedTableCell>
            <FriedTableCell class="hidden-sm-and-down">
              {{ getDateTime(fromMillis(file.createdAt)) }}
            </FriedTableCell>
            <FriedTableCell class="hidden-md-and-down">
              {{ file.extension.toUpperCase() }}
            </FriedTableCell>
            <FriedTableCell class="hidden-md-and-down">
              {{ formatSize(file.size) }}
            </FriedTableCell>
            <FriedTableCell
              v-if="!participantId && isRoom"
              :align="TableAlign.Right"
              class="hidden-md-and-down"
            >
              {{ getNumberOfDownloads(file) }}
            </FriedTableCell>
            <FriedTableCell :align="TableAlign.Right" class="menu-cell">
              <div class="menu-button-container">
                <FriedIconButton
                  v-tooltip="{
                    content: isFileUploading(file.status) ? tooltipIsUploading : t('download'),
                    placement: Placement.Top,
                    distance: 8,
                    delay: { show: 300, hide: 100 },
                  }"
                  :disabled="isFileUploading(file.status)"
                  :data-external="`file-list-file-id:${file.id}-download-button`"
                  :class="{ 'hidden-sm-and-down': !participantId && hasCollaboratorAccess }"
                  @click="$emit('download', file)"
                >
                  <FriedIcon icon="download" />
                </FriedIconButton>
                <FriedDropdown
                  v-if="!participantId && (hasCollaboratorAccess || isAdmin)"
                  :main-axis-offset="0"
                  :cross-axis-offset="8"
                  placement="bottom-end"
                  :close-on-action="true"
                >
                  <template #trigger>
                    <FriedIconButton
                      v-tooltip="{
                        content: t('files_more_tooltip'),
                        placement: Placement.Top,
                        distance: 8,
                        delay: { show: 300, hide: 100 },
                      }"
                      data-external="file-options-menu-button"
                    >
                      <FriedIcon icon="ellipsis" />
                    </FriedIconButton>
                  </template>
                  <template #content>
                    <FriedDropdownItem
                      v-for="option in getFileOptions(
                        file.pinned,
                        canEditFile(file),
                        hasCollaboratorAccess,
                        isRoom
                      )"
                      :key="option.callback"
                      :class="option.class"
                      :disabled="isFileOptionDisabled(file, option)"
                      :data-external="`file:${file.id}-${option.title}-option`"
                      @click="handleSelect(option.callback, file)"
                    >
                      <template #leftIcon>
                        <FriedIcon :icon="option.icon" />
                      </template>
                      <FriedParagraph>{{ option.title }}</FriedParagraph>
                    </FriedDropdownItem>
                  </template>
                </FriedDropdown>
                <FriedIconButton
                  v-else-if="participantId && isCurrentUserFileOwner(file)"
                  :data-external="`file-list-file-id:${file.id}-delete-button`"
                  @click="handleDelete(file)"
                >
                  <FriedIcon icon="delete-bin" />
                </FriedIconButton>
              </div>
            </FriedTableCell>
          </FriedTableRow>
        </template>
      </FriedTable>
      <TablePagination
        class="pagination"
        :total-count="totalFiles"
        :offset="filter.offset"
        :items-per-page="filter.limit"
        @update="handleLoadPage"
      />
    </template>
    <Prompt
      v-if="showDeleteModal"
      :title="t('files_are_you_sure')"
      :text="t('files_modal_paragraph')"
      :button-type="ButtonType.Danger"
      @close="showDeleteModal = false"
      @cancel="showDeleteModal = false"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<script lang="ts">
import type { Ref, PropType } from 'vue';
import { computed, defineComponent, onMounted, ref, nextTick, onBeforeUnmount, watch } from 'vue';
import { SortOrder, TableAlign, Placement, AvatarSize, ButtonType } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import TablePagination from '@getaccept/lib-shared/src/table/TablePagination.vue';
import Prompt from '@getaccept/lib-shared/src/components/Prompt.vue';
import { DateTime } from 'luxon';
import EmptyStateTable from '@getaccept/lib-shared/src/table/EmptyStateTable.vue';
import { LibraryHelper } from '@getaccept/lib-shared/src/library/helpers/library.helper';
import type { OptionMenuItem } from '@getaccept/lib-shared/src/options-menu/types/option-menu-item';
import { useRoute, useRouter } from 'vue-router';
import { FileSortBy } from '../types/enums/file-sort-by';
import type { DealroomFile } from '../types/dealroom-file';
import type { Participant } from '../../types';
import type { LoadFilesPayload } from '../types/load-files-payload';
import FileConstants from '../constants/file.constants';
import FileHelper from '../helpers/file.helper';
import { FileMenuOption } from '../types/enums/file-menu-option';
import { FileStatus } from '../types/enums/file-status';
import AvatarHelper from '../../helpers/avatar.helper';
import InternalNavigationHelper from '../../helpers/internal-navigation.helper';
import type { TemplateFile } from '../../template-file/types/template-file';
import type { LoadTemplateFilesPayload } from '../../template-file/types/load-template-files-payload';
import FileSkeleton from './FileSkeleton.vue';
import ParticipantInfo from './ParticipantInfo.vue';

export default defineComponent({
  components: {
    EmptyStateTable,
    FileSkeleton,
    ParticipantInfo,
    TablePagination,
    Prompt,
  },
  props: {
    files: {
      type: Array as PropType<DealroomFile[] | TemplateFile[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
    },
    totalFiles: {
      type: Number,
      default: 0,
    },
    filter: {
      type: Object as PropType<LoadFilesPayload | LoadTemplateFilesPayload>,
      default: () => FileConstants.defaultPayload,
    },
    participantId: {
      type: String,
      default: '',
    },
    isRoomOwner: { type: Boolean },
    hasCollaboratorAccess: { type: Boolean },
    currentUserId: {
      type: String,
      default: '',
    },
    participants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    isAdmin: { type: Boolean },
    searchLoading: { type: Boolean },
    isSearching: { type: Boolean },
    isRoom: { type: Boolean },
  },
  emits: ['download', 'delete', 'load', 'upload', 'pin', 'un-pin', 'open-statistics'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const hasFiles = computed(() => props.files.length > 0);
    const fileToDelete: Ref<DealroomFile | TemplateFile> = ref(null);
    const showDeleteModal = ref(false);

    const getFileOwner = (file: DealroomFile | TemplateFile) => {
      const uploadedId = 'participantId' in file ? file.participantId : file.userId;
      return (
        props.participants.find(participant => participant.id === uploadedId) || {
          id: undefined,
        }
      );
    };

    const getNumberOfDownloads = (file: DealroomFile | TemplateFile) =>
      'nbrOfDownloads' in file ? file.nbrOfDownloads : 0;

    const isActiveSortBy = (key: FileSortBy) => key === props.filter.sortBy;

    const isFileUploading = (status: FileStatus) => status === FileStatus.Uploading;

    const getToggledSortOrder = (sortBy: FileSortBy) => {
      switch (true) {
        case sortBy !== props.filter.sortBy:
          return { sortBy, sortOrder: SortOrder.Desc };
        case props.filter.sortOrder === SortOrder.Asc:
          return { sortBy: FileSortBy.PinnedAt, sortOrder: SortOrder.Desc };
        default:
          return { sortBy, sortOrder: SortOrder.Asc };
      }
    };

    const handleSort = (newSortBy: FileSortBy) => {
      emit('load', getToggledSortOrder(newSortBy));
    };

    const isCurrentUserFileOwner = (file: DealroomFile | TemplateFile) => {
      const uploadedId = 'participantId' in file ? file.participantId : file.userId;

      if (uploadedId === props.currentUserId && props.hasCollaboratorAccess) {
        return true;
      }
      return uploadedId === props.participantId;
    };

    const canEditFile = (file: DealroomFile | TemplateFile) => {
      if (props.isRoomOwner) {
        return true;
      }
      return isCurrentUserFileOwner(file);
    };

    const handleLoadPage = (value: number) => {
      emit('load', { offset: props.filter.offset + value });
    };

    const handleDelete = (file: DealroomFile | TemplateFile) => {
      fileToDelete.value = file;
      showDeleteModal.value = true;
    };

    const handleSelect = (option: FileMenuOption, file: DealroomFile | TemplateFile) => {
      if (option === FileMenuOption.Delete) {
        handleDelete(file);
        return;
      }
      emit(option, file);
    };

    const handleDeleteConfirm = () => {
      emit('delete', fileToDelete.value.id);
      showDeleteModal.value = false;
      fileToDelete.value = null;
    };

    const isFileOptionDisabled = (file: DealroomFile | TemplateFile, option: OptionMenuItem) =>
      (isFileUploading(file.status) && option.callback === FileMenuOption.Download) ||
      option.disabled;

    const hasNoFiles = computed(
      () => !hasFiles.value && !props.searchLoading && !props.isSearching
    );

    const hasNoSearchResults = computed(
      () => !hasFiles.value && !props.searchLoading && props.isSearching
    );

    const tooltipIsUploading = computed(() =>
      props.isRoom
        ? t('download_disabled_until_done_processing')
        : t('download_disabled_template_file_refresh')
    );

    const route = useRoute();
    const router = useRouter();

    onMounted(async () => {
      await nextTick();
      InternalNavigationHelper.handleFilesScroll(document, route);
    });

    watch(
      () => route.query.fileId,
      () => {
        InternalNavigationHelper.handleFilesScroll(document, route);
      }
    );

    onBeforeUnmount(() => {
      router.replace({
        params: { ...route.params },
        query: { ...route.query, fileId: undefined },
      });
    });

    return {
      hasFiles,
      getFileOwner,
      handleSort,
      isActiveSortBy,
      handleLoadPage,
      isCurrentUserFileOwner,
      handleSelect,
      handleDelete,
      handleDeleteConfirm,
      getToggledSortOrder,
      getDisplayName: AvatarHelper.getDisplayName,
      getDateTime: FileHelper.getDateTime,
      fromMillis: DateTime.fromMillis,
      formatSize: FileHelper.formatSize,
      getTruncatedText: LibraryHelper.getTruncatedText,
      fileToDelete,
      showDeleteModal,
      getFileOptions: FileHelper.getOptions,
      isFileUploading,
      FileSortBy,
      TableAlign,
      Placement,
      AvatarSize,
      ButtonType,
      FileMenuOption,
      t,
      canEditFile,
      isFileOptionDisabled,
      hasNoFiles,
      hasNoSearchResults,
      getNumberOfDownloads,
      tooltipIsUploading,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@getaccept/lib-shared/src/assets/scss/truncate-text';
@import '../../scss/removed-participant';
@import '@getaccept/dsr-shared/src/scss/blink';

.base-table {
  .base-table-body {
    tr {
      &.is-pinned {
        background: var(--gray-98);
      }
    }
  }
}

.file-list-table {
  width: 100%;

  th {
    white-space: nowrap;
  }

  .name {
    display: flex;
    align-items: center;
    gap: var(--spacing-50);
  }

  .pinned,
  .name {
    .svg-icon {
      fill: var(--base-blue);
      flex-shrink: 0;
    }
  }

  .name-header-cell {
    width: 26%;

    @media screen and (max-width: $sm) {
      width: 80%;
    }
  }
}

.menu-cell {
  .menu-button-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--spacing-50);
  }

  @media screen and (max-width: $sm) {
    display: table-cell;
  }
}

.pagination {
  @media screen and (max-width: $xs) {
    justify-content: flex-start;
    margin: var(--spacing-100) var(--spacing-100) 0;
  }
}

.delete-option {
  &.dropdown-item-container &:not(.disabled) {
    .button-icon {
      .svg-icon {
        color: var(--red-42);
      }
    }

    .dropdown-item-content {
      .paragraph {
        color: var(--red-42);
      }
    }
  }
}
</style>
